import { Device, i18n, waitAFrame } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Sprite } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { PaintMeta } from '../game/systems/ShopSystem';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { PaintSelector } from '../ui/PaintSelector';
import { PurchasePanel } from '../ui/PurchasePanel';
import { StatsWidget } from '../ui/StatsWidget';
import { SwipeScroller } from '../ui/SwipeScroller';
import { PaintIcon } from './../ui/PaintIcon';
import { RadioSelectorStyles } from './../ui/RadioSelector';
import { ShopPanel } from './../ui/ShopPanel';
import { RideStudioBaseShopScreen, RideStudioBaseShopScreenOptions } from './RideStudioBaseShopScreen';

export class RideStudioWheelShopScreen extends RideStudioBaseShopScreen
{
    protected options: RideStudioBaseShopScreenOptions;
    protected selectedText!: LocalisedText;
    protected tickIcon!: Sprite;

    private _currentLeftID: string;
    private _currentRightID: string;
    private _selectContainer!: Sprite;

    private _currentLeftWheelColor: string;
    private _currentRightWheelColor: string;
    private _currentActiveColorIndex: number;
    
    private _startLeftID: string;
    private _startRightID: string;
    private _startLeftWheelColor: string;
    private _startRightWheelColor: string;
    private _startActiveColorIndex: number;

    private _paintSelector!: PaintSelector;
    private _paintPurchaseContainer!: Sprite;
    // private _paintPurchaseBtn?: PurchasePanel; // Seems not being used anymore
    private _stats: StatsWidget;

    private _leftSwipeScroller!: SwipeScroller;

    constructor(app: App, options: RideStudioBaseShopScreenOptions)
    {
        super(app, options);

        this.type = 'wheel';
        this._currentLeftID = '';
        this._currentRightID = '';

        this._currentLeftWheelColor = '';
        this._currentRightWheelColor = '';
        this._currentActiveColorIndex = 0;
        
        this._startLeftID = '';
        this._startRightID = '';
        this._startLeftWheelColor = '';
        this._startRightWheelColor = '';
        this._startActiveColorIndex = 0;

        this.options = options;

        // resize and re-order icons on settings change for consistency across app
        Shared.settings.shopIconSizeChanged.connect((newSize) =>
        {
            if (!this._paintSelector) return;

            this._paintSelector.radioSelector.resizeButtons(newSize);
            gsap.killTweensOf(this._paintSelector.view);
            this._paintSelector.view.y = 0;
        });

        this._stats = new StatsWidget();
        this.view.addChild(this._stats.view);
    }

    public show(): void
    {
        super.show();
        
        this.app.analytics.trackState(AnalyticsState.WHEELS);
        
        !Settings.spineDebug && this._stats.open();
        this.selectedText.visible = !Settings.spineDebug;
        this.tickIcon.visible = !Settings.spineDebug;
        this._selectContainer.visible = !Settings.spineDebug;
        this._paintPurchaseContainer.visible = !Settings.spineDebug;

        this.leftPanel.titleText.setText('rideStudioWheelShopScreen_type');
        this.rightPanel.titleText.setText('rideStudioWheelShopScreen_paint');

        const activeIndex = this.leftPanel.radioSelector?.getActiveIndex();

        this.updateWheelIcon(activeIndex === 0 || activeIndex === 1);
        
        this._currentLeftWheelColor = Shared.game.garage.selectedColorWheelLeft;
        this._currentRightWheelColor = Shared.game.garage.selectedColorWheelRight;
        this._currentActiveColorIndex = Shared.game.garage.colors.indexOf(
            this.leftPanel.radioSelector?.getActiveIndex() === 2
                ? this._currentRightWheelColor
                : this._currentLeftWheelColor,
        );

        this._paintSelector.radioSelector.setSelected(this._currentActiveColorIndex);
        this._paintSelector.radioSelector.setActive(this._currentActiveColorIndex);

        this._currentLeftID = Shared.game.garage.selectedWheelLeft;
        this._currentRightID = Shared.game.garage.selectedWheelRight;
        
        this._startLeftID = this._currentLeftID;
        this._startRightID = this._currentRightID;
        this._startLeftWheelColor = this._currentLeftWheelColor;
        this._startRightWheelColor = this._currentRightWheelColor;
        this._startActiveColorIndex = this._currentActiveColorIndex;
        
        this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
        this._leftSwipeScroller.refreshContent();
        this._paintSelector.snapToSelected(false);
    }

    public hide(): void
    {
        this._stats.close();
        
    }

    public update(delta = 1): void
    {
        this._paintSelector.update(delta);
    }

    protected buildLeftPanel(): void
    {
        this.leftPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [
                {
                    iconOn: 'selector-left-wheel',
                    accessibleTitle: 'radio',
                    tabIndex: 1,
                    active: true,
                },
                {
                    iconOn: 'selector-both-wheels',
                    accessibleTitle: 'radio',
                    tabIndex: 1,
                },
                {
                    iconOn: 'selector-right-wheel',
                    accessibleTitle: 'radio',
                    tabIndex: 1,
                },
            ],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.leftPanel.view);

        this._leftSwipeScroller = this.createSwipeScroller();
        this.leftPanel.content.addChild(this._leftSwipeScroller.view);

        // connect buttons
        this.leftPanel.signals.onArrowUp.connect(() =>
        {
            this._leftSwipeScroller.nextItem();

            //this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });
        this.leftPanel.signals.onArrowDown.connect(() =>
        {
            this._leftSwipeScroller.prevItem();

            //this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });

        this.leftPanel.signals.onRadioUpdate.connect((btn, index) =>
        {
            if(this._currentLeftWheelColor && !Shared.game.shop.checkPurchased(Shared.game.garage.selectedColorWheelLeft, 'wheel')){
                Shared.game.shop.setColorWheelLeft(this._currentLeftWheelColor);
            }
            
            if(this._currentRightWheelColor && !Shared.game.shop.checkPurchased(Shared.game.garage.selectedColorWheelRight, 'wheel')){
                Shared.game.shop.setColorWheelRight(this._currentRightWheelColor);
            }
            
            this.updateWheelIcon(index === 0 || index === 1);
            this.displayItemUnlockedStatus(index !== 2);

            if (this._paintSelector)
            {
                this._paintSelector.radioSelector.setSelected(Shared.game.garage.colors.indexOf(
                    index === 2
                        ? this._currentRightWheelColor
                        : this._currentLeftWheelColor,
                ));
                this._paintSelector.radioSelector.setActive(Shared.game.garage.colors.indexOf(
                    index === 2
                        ? this._currentRightWheelColor
                        : this._currentLeftWheelColor,
                ));
            }
            
            this.checkEnableConfirmButton();
        });

        const selectIcon = new Sprite();
        const selectBgWidth = 180;
        const selectBgHeight = 60;

        selectIcon.anchor.set(0.5);

        this.selectedText = new LocalisedText('rideStudioWheelShopScreen_selectedText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 250,
        });

        this.selectedText.anchor.set(0.5);
        this.selectedText.y = 65;
        this.leftPanel.view.addChild(this.selectedText);
        
        const selectText = new LocalisedText('rideStudioWheelShopScreen_selectText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 165,
        });

        selectText.anchor.set(0.5);
        selectIcon.addChild(selectText);
        
        this.tickIcon = Sprite.from(Shared.assets.getTexture('equipped-tick'));
        this.tickIcon.scale.set(1.25);
        this.tickIcon.anchor.set(0.5);
        this.tickIcon.position.set(-60, 30);
        this.leftPanel.view.addChild(this.tickIcon);

        this._selectContainer = new Sprite();
        this._selectContainer.anchor.set(0.5);
        this._selectContainer.y = 280;
        this.leftPanel.addChild(this._selectContainer);
        this.leftPanel.radioSelector?.setActive(1);
        this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
    }

    protected buildRightPanel(): void
    {
        this.rightPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.rightPanel.view);

        const padding = 6;

        this._paintSelector = new PaintSelector({
            buttonType: PaintIcon as any,
            style: RadioSelectorStyles.inlineWrap,
            padding,
            inlineWrapLimit: 300,
            buttons: this._createPaintButtonMeta(),
            containerHeight: 290,
            width: 274,
            height: 454,
            cancelCallback: () => this._cancelPaintPurchase(),
        });

        Shared.game.shop.signals.colorUnlocked.connect((colorId) =>
        {
            // eslint-disable-next-line max-len
            (this._paintSelector.radioSelector._buttons[Shared.game.garage.colors.indexOf(colorId)] as any as PaintIcon).unlocked = true;
        });

        this.rightPanel.content.addChild(this._paintSelector.view);

        this._paintPurchaseContainer = new Sprite();
        this._paintPurchaseContainer.anchor.set(0.5);
        this._paintPurchaseContainer.y = 280;
        this.rightPanel.addChild(this._paintPurchaseContainer);

        this.rightPanel.signals.onArrowDown.connect(() =>
        {
            this._paintSelector.moveUp();
        });
        this.rightPanel.signals.onArrowUp.connect(() =>
        {
            this._paintSelector.moveDown();
        });
        
        this._currentLeftWheelColor = Shared.game.garage.selectedColorWheelLeft;
        this._currentRightWheelColor = Shared.game.garage.selectedColorWheelRight;
        this._currentActiveColorIndex = Shared.game.garage.colors.indexOf(
            this.leftPanel.radioSelector?.getActiveIndex() === 2
                ? this._currentRightWheelColor
                : this._currentLeftWheelColor,
        );
        this._paintSelector.radioSelector.setSelected(this._currentActiveColorIndex);
        this._paintSelector.radioSelector.setActive(this._currentActiveColorIndex);
    }

    protected updateWheelIcon(left: boolean): void
    {
        const garage = Shared.game.garage;
        const wheelIndex = garage.wheels.indexOf(left ? garage.selectedWheelLeft : garage.selectedWheelRight);

        this._leftSwipeScroller.setCurrentIndex(wheelIndex);
        this._leftSwipeScroller.refreshContent();
    }

    private _createPaintButtonMeta(): any[]
    {
        const paintButtons: any[] = [];
        const shopSystem = Shared.game.shop;
        const paintMeta = shopSystem.getPaintMeta();

        paintMeta.forEach((meta) =>
        {
            paintButtons.push({
                ...meta,
                onClick: () =>
                {
                    const activeIndex = this.leftPanel.radioSelector?.getActiveIndex();

                    this._paintSelector.radioSelector.setActive(meta.index);

                    switch (activeIndex)
                    {
                        case 0:
                            shopSystem.setColorWheelLeft(meta.id);
                            
                            if (shopSystem.checkPurchased(meta.id, 'color'))
                            {
                                this._currentLeftWheelColor = meta.id;
                                this._paintSelector.radioSelector.setSelected(meta.index);
                            }
                            else{
                                this._paintSelector.radioSelector.setSelected(-1);
                            }
                            break;
                        case 1:
                            shopSystem.setColorWheelLeft(meta.id);
                            shopSystem.setColorWheelRight(meta.id);
                            
                            if (shopSystem.checkPurchased(meta.id, 'color'))
                            {
                                this._currentLeftWheelColor = meta.id;
                                this._currentRightWheelColor = meta.id;
                                this._paintSelector.radioSelector.setSelected(meta.index);
                            }
                            else{
                                this._paintSelector.radioSelector.setSelected(-1);
                            }
                            break;
                        case 2:
                            shopSystem.setColorWheelRight(meta.id);
                            
                            if (shopSystem.checkPurchased(meta.id, 'color'))
                            {
                                this._currentRightWheelColor = meta.id;
                                this._paintSelector.radioSelector.setSelected(meta.index);
                            }
                            else{
                                this._paintSelector.radioSelector.setSelected(-1);
                            }
                            break;
                    }

                    const leftActiveIndex = this.leftPanel.radioSelector?.getActiveIndex();

                    this.updateWheelIcon(leftActiveIndex === 0 || leftActiveIndex === 1);

                    this._createPurchasePanel(meta);
                    
                    this.checkEnableConfirmButton();
                },

            });
        });

        return paintButtons;
    }

    protected displayItemUnlockedStatus(left: boolean): void
    {
        if (Settings.spineDebug) return;

        this._selectContainer.removeChildren();
        const unlocked = left
            ? Shared.game.shop.checkPurchased(Shared.game.garage.selectedWheelLeft, 'wheel')
            : Shared.game.shop.checkPurchased(Shared.game.garage.selectedWheelRight, 'wheel');

        const data = left
            ? this.app.data.getWheelData(Shared.game.garage.selectedWheelLeft)
            : this.app.data.getWheelData(Shared.game.garage.selectedWheelRight);

        if (unlocked)
        {
            this._currentLeftID = Shared.game.garage.selectedWheelLeft;
            this._currentRightID = Shared.game.garage.selectedWheelRight;
            this.selectedText.visible = true;
            this.tickIcon.visible = true;
            // if (left)
            // {
                
            //     this.selectedText.visible = true;// unlocked && (this._currentLeftID === Shared.game.garage.selectedWheelLeft);
            //     this.tickIcon.visible = true;
            // }
            // else
            // {
            //     this.selectedText.visible = true;//unlocked && (this._currentRightID === Shared.game.garage.selectedWheelRight);
            //     this.tickIcon.visible = true;
            // }
        }
        else
        {
            const shopSystem = Shared.game.shop;
            const purchase = new PurchasePanel({
                enableCoinPurchase: true,
                enableDiamondPurchase: true,
                enableCancel: false,
                unlockCoinCost: data.coinPrice ?? 80,
                unlockDiamondCost: data.diamondPrice ?? 8,
            });

            this.selectedText.visible = false;
            this.tickIcon.visible = false;

            purchase.signals.coinBtnClick.connect((cost) =>
            {
                if (shopSystem.checkCoinsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: this.leftPanel.radioSelector?.getActiveIndex() !== 2
                            ? Shared.game.garage.getPlainSprite({
                                wheelLeft: Shared.game.garage.selectedWheelLeft,
                                color: Shared.game.garage.selectedColorWheelLeft,
                            })
                            : Shared.game.garage.getPlainSprite({
                                wheelRight: Shared.game.garage.selectedWheelRight,
                                color: Shared.game.garage.selectedColorWheelRight,
                            }),
                        coinCost: cost,
                        onConfirm: () =>
                        {
                            switch (this.leftPanel.radioSelector?.getActiveIndex())
                            {
                                case 0: {
                                    this._purchaseWheel(Shared.game.garage.selectedWheelLeft, cost);
                                    this._currentLeftID = Shared.game.garage.selectedWheelLeft;
                                    break;
                                }
                                case 1: {
                                    this._purchaseWheel(Shared.game.garage.selectedWheelLeft, cost);
                                    this._currentLeftID = Shared.game.garage.selectedWheelLeft;
                                    this._currentRightID = Shared.game.garage.selectedWheelRight;
                                    break;
                                }
                                case 2: {
                                    this._purchaseWheel(Shared.game.garage.selectedWheelRight, cost);
                                    this._currentRightID = Shared.game.garage.selectedWheelRight;
                                    break;
                                }
                            }

                            Shared.game.garage.saveVehicleSelection();
                            this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
                            this._selectContainer.removeChildren();
                            this._leftSwipeScroller.refreshContent();
                        },
                    });
                }
            });

            purchase.signals.diamondBtnClick.connect((cost) =>
            {
                if (shopSystem.checkDiamondsAvailable(cost))
                {
                    if (shopSystem.checkCoinsAvailable(cost))
                    {
                        this.app.popup.presentConfirmPurchasePopup({
                            title: 'purchasePopup_title',
                            message: 'purchasePopup_message_generic',
                            confirm: 'purchasePopup_confirm',
                            cancel: 'purchasePopup_cancel',
                            plainSprite: this.leftPanel.radioSelector?.getActiveIndex() !== 2
                                ? Shared.game.garage.getPlainSprite({
                                    wheelLeft: Shared.game.garage.selectedWheelLeft,
                                    color: Shared.game.garage.selectedColorWheelLeft,
                                })
                                : Shared.game.garage.getPlainSprite({
                                    wheelRight: Shared.game.garage.selectedWheelRight,
                                    color: Shared.game.garage.selectedColorWheelRight,
                                }),
                            diamondCost: cost,
                            onConfirm: () =>
                            {
                                switch (this.leftPanel.radioSelector?.getActiveIndex())
                                {
                                    case 0: {
                                        this._purchaseWheel(Shared.game.garage.selectedWheelLeft, 0, cost);
                                        this._currentLeftID = Shared.game.garage.selectedWheelLeft;
                                        break;
                                    }
                                    case 1: {
                                        this._purchaseWheel(Shared.game.garage.selectedWheelLeft, 0, cost);
                                        this._currentLeftID = Shared.game.garage.selectedWheelLeft;
                                        this._currentRightID = Shared.game.garage.selectedWheelRight;
                                        break;
                                    }
                                    case 2: {
                                        this._purchaseWheel(Shared.game.garage.selectedWheelRight, 0, cost);
                                        this._currentRightID = Shared.game.garage.selectedWheelRight;
                                        break;
                                    }
                                }
                                Shared.game.garage.saveVehicleSelection();
                                this.displayItemUnlockedStatus(this.leftPanel.radioSelector?.getActiveIndex() !== 2);
                                this._selectContainer.removeChildren();
                                this._leftSwipeScroller.refreshContent();
                            },
                        });
                    }
                }
            });

            this._selectContainer.addChild(purchase.view);
        }
        
        this.checkEnableConfirmButton();
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this._stats.view.x = this.app.stageCenter.x + 55;
        this._stats.view.y = this.app.stageBottom - 300;
    }
    
    // ==== SWIPE SCROLLER ========================================================================

    private createSwipeScroller(): SwipeScroller
    {
        const scroller = new SwipeScroller({
            length: Shared.game.garage.wheels.length,
            offsetY: -10,
            width: 274,
            height: 454,
            spacing: 150,
        }, this.leftSwipeScrollerContentProvider.bind(this));

        scroller.onSnap.connect(this.swipeScrollerSnapHandler.bind(this));
        scroller.onStartDrag.connect(this.swipeScrollerStartDragHandler.bind(this));

        return scroller;
    }

    /**
     * Provide sprites to a swipe scroller
     * @param index - Reference index for the content
     * @returns Sprite to be used inside the scroller
     */
    private leftSwipeScrollerContentProvider(index: number): Container
    {
        const leftActiveIndex = this.leftPanel.radioSelector?.getActiveIndex();
        const left = leftActiveIndex === 0 || leftActiveIndex === 1;
        const garage = Shared.game.garage;

        const newContent = left
            ? garage.getPlainSprite({ wheelLeft: garage.wheels[index], color: garage.selectedColorWheelLeft })
            : garage.getPlainSprite({ wheelRight: garage.wheels[index], color: garage.selectedColorWheelRight });

        const unlocked = Shared.game.shop.checkPurchased(garage.wheels[index], 'wheel');
        const selected = garage.wheels[index] === (leftActiveIndex === 2 ? this._currentRightID : this._currentLeftID);

        newContent.scale.set(100 / newContent.width);

        if (!selected && !unlocked)
        {
            const lock = Sprite.from(Shared.assets.getTexture('icon-locked'));

            lock.scale.set(0.75 / newContent.scale.x);
            lock.anchor.set(0.5);
            lock.position.set(-30 / newContent.scale.x, 30 / newContent.scale.x);

            newContent.addChild(lock);
        }

        return newContent;
    }

    /** Fires when scroller snaps to one of its items */
    private swipeScrollerSnapHandler(scroller: SwipeScroller): void
    {
        const garage = Shared.game.garage;

        const activeIndex = this.leftPanel.radioSelector?.getActiveIndex();
        const index = scroller.getCurrentIndex();
        const wheel = Shared.game.garage.wheels[index];

        switch (activeIndex)
        {
            case 0 :
                garage.setWheelLeft(wheel);
                break;
            case 1 :
                garage.setWheelRight(wheel);
                garage.setWheelLeft(wheel);
                break;
            case 2 :

                garage.setWheelRight(wheel);
                break;
        }

        this.displayItemUnlockedStatus(activeIndex === 0 || activeIndex === 1);
    }

    /** Fires when user start dragging the scroller */
    private swipeScrollerStartDragHandler(): void
    {
        this.selectedText.visible = false;
        this.tickIcon.visible = false;
        this._selectContainer.removeChildren();
    }

    private _createPurchasePanel(meta: PaintMeta): void
    {
        this._paintPurchaseContainer.removeChildren();
        const unlocked = Shared.game.shop.checkPurchased(meta.id, 'color');

        if (unlocked)
        {
            return;
        }

        const shopSystem = Shared.game.shop;
        const purchase = new PurchasePanel({
            enableCoinPurchase: !!meta.coinCost,
            enableDiamondPurchase: !!meta.diamondCost,
            unlockCoinCost: meta.coinCost,
            unlockDiamondCost: meta.diamondCost,
            enableCancel: false,
        });

        purchase.signals.cancelBtnClick.connect(() => this._cancelPaintPurchase());

        purchase.signals.coinBtnClick.connect(() =>
        {
            if (shopSystem.checkCoinsAvailable(meta.coinCost))
            {
                this.app.popup.presentConfirmPurchasePopup({
                    title: 'purchasePopup_title',
                    message: 'purchasePopup_message_paint',
                    confirm: 'purchasePopup_confirm',
                    cancel: 'purchasePopup_cancel',
                    tint: meta.tint,
                    itemSprite: 'paint-tint-storeitem',
                    coinCost: meta.coinCost,
                    onConfirm: () => this._purchasePaint(meta.id, meta.index, meta.coinCost)
                });
            }
        });

        purchase.signals.diamondBtnClick.connect(() =>
        {
            if (shopSystem.checkDiamondsAvailable(meta.diamondCost))
            {
                this.app.popup.presentConfirmPurchasePopup({
                    title: 'purchasePopup_title',
                    message: 'purchasePopup_message_paint',
                    confirm: 'purchasePopup_confirm',
                    cancel: 'purchasePopup_cancel',
                    tint: meta.tint,
                    itemSprite: 'paint-tint-storeitem',
                    diamondCost: meta.diamondCost,
                    onConfirm: () => this._purchasePaint(meta.id, meta.index, 0, meta.diamondCost)
                });
            }
        });

        this._paintPurchaseContainer.addChild(purchase.view);
    }
    
    private _purchasePaint(id: string, index: number, coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(id, 'color', coins, diamonds);
        // eslint-disable-next-line max-len
        (this._paintSelector.radioSelector._buttons[Shared.game.garage.colors.indexOf(id)] as any as PaintIcon).unlocked = true;
        const activeIndex = this.leftPanel.radioSelector?.getActiveIndex();

        switch (activeIndex)
        {
            case 0:
                this._currentLeftWheelColor = id;
                break;
            case 1:
                this._currentLeftWheelColor = id;
                this._currentRightWheelColor = id;
                break;
            case 2:
                this._currentRightWheelColor = id;
                break;
        }
        this._currentActiveColorIndex = this._paintSelector.radioSelector.getActiveIndex();
        Shared.game.garage.saveVehicleSelection();
        this._paintSelector.radioSelector.setSelected(index);
        this._paintSelector.radioSelector.setActive(index);
        this._paintPurchaseContainer.removeChildren();
        Shared.game.shop.signals.colorUnlocked.emit(id);
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `color|${id}`});
        
        if(shopSystem.areAllPaintsUnlocked()){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `color|${id}`, content_type: 'wheel|paint'});
        }
        
        this.checkEnableConfirmButton();
    }
    
    private _purchaseWheel(id: string, coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(id, 'wheel', coins, diamonds);
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `wheel|${id}`});
        
        if(shopSystem.checkAllTypeUnlocked("wheel")){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `wheel|${id}`, content_type: 'wheel'});
        }
    }

    private async _cancelPaintPurchase(): Promise<void>
    {
        const shopSystem = Shared.game.shop;

        // hack, some sort of race condition occurs here
        // waiting for a few frames works around the issue
        await waitAFrame(5);

        const activeIndex = this.leftPanel.radioSelector?.getActiveIndex();

        switch (activeIndex)
        {
            case 0:
                shopSystem.setColorWheelLeft(this._currentLeftWheelColor);
                break;
            case 1:
                shopSystem.setColorWheelLeft(this._currentLeftWheelColor);
                shopSystem.setColorWheelRight(this._currentRightWheelColor);
                break;
            case 2:
                shopSystem.setColorWheelRight(this._currentRightWheelColor);
                break;
        }
        const leftActiveIndex = this.leftPanel.radioSelector?.getActiveIndex();

        this.updateWheelIcon(leftActiveIndex === 0 || leftActiveIndex === 1);
        this._paintSelector.radioSelector.setActive(this._currentActiveColorIndex);
        this._paintPurchaseContainer.removeChildren();
    }
    
    private checkEnableConfirmButton(){
        const unlockedLeftWheel = Shared.game.shop.checkPurchased(Shared.game.garage.selectedWheelLeft, 'wheel');
        const unlockedRightWheel = Shared.game.shop.checkPurchased(Shared.game.garage.selectedWheelRight, 'wheel');
        const unlockedLeftWheelColor = Shared.game.shop.checkPurchased(Shared.game.garage.selectedColorWheelLeft, 'color');
        const unlockedRightWheelColor = Shared.game.shop.checkPurchased(Shared.game.garage.selectedColorWheelRight, 'color');
        
        if(unlockedLeftWheel && unlockedRightWheel && unlockedLeftWheelColor && unlockedRightWheelColor){
            this.confirmButton.enable();
        }
        else{
            this.confirmButton.disable();
        }
        
    }
    
    protected onCancelButtonPressed(): void {
        super.onCancelButtonPressed();
        
        this._paintSelector.radioSelector.setActive(this._startActiveColorIndex);
        this._paintSelector.radioSelector.setSelected(this._startActiveColorIndex);
        
        Shared.game.shop.setWheelLeft(this._startLeftID);
        Shared.game.shop.setWheelRight(this._startRightID);
        Shared.game.shop.setColorWheelLeft(this._startLeftWheelColor);
        Shared.game.shop.setColorWheelRight(this._startRightWheelColor);
        
    }
    
    protected onConfirmButtonPressed(): void {
        super.onConfirmButtonPressed();
        
        Shared.game.shop.setWheelLeft(this._currentLeftID);
        Shared.game.shop.setWheelRight(this._currentRightID);
        Shared.game.shop.setColorWheelLeft(this._currentLeftWheelColor);
        Shared.game.shop.setColorWheelRight(this._currentRightWheelColor);
        
        Shared.game.garage.saveVehicleSelection();
    }
}
